// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides functionality to update organization user details in the remote API.
 */
import * as z from 'zod';
import { organizationUserSchema } from '@/account/api/types';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { Currency } from '@/currency/api/types';
import { serializeOptionalCurrencyForAPI } from '@/currency/utilities/serialize';

/**
 * Schema for validating and transforming the API response when updating an organization user.
 * Restructures the response to expose the user directly.
 */
const updateOrganizationUserResponseSchema = z
  .object({
    data: organizationUserSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, user: data }));

/** Type representing the validated response from updating an organization user */
type UpdateOrganizationUserResponse = z.infer<
  typeof updateOrganizationUserResponseSchema
>;

/**
 * Interface defining the parameters that can be updated for an organization user.
 * All fields are optional to allow partial updates.
 * @property firstName - The first name of the user.
 * @property lastName - The last name of the user.
 * @property defaultRate - The default billing rate for the user as a currency object.
 * @property userGroup - The user group for the user.
 */
export interface UpdateOrganizationUserParameters {
  firstName?: string | null;
  lastName?: string | null;
  defaultRate?: Currency | null;
  userGroup?: string;
}

/**
 * Updates an organization user record in the remote API.
 *
 * @param organizationKey - The key of the organization.
 * @param userKey - The key of the user to update.
 * @param parameters - The parameters to update, which can include firstName, lastName, defaultRate, and userGroup.
 * @returns A promise that resolves to the validated response containing the updated user.
 * @throws Will throw if the API request fails or if the response fails validation.
 */
export async function updateOrganizationUser(
  organizationKey: string,
  userKey: string,
  parameters: UpdateOrganizationUserParameters
): Promise<UpdateOrganizationUserResponse> {
  const url = `/v1/organization/${organizationKey}/users/${userKey}`;
  const response = await api.patch(url, {
    ...parameters,
    defaultRate: serializeOptionalCurrencyForAPI(parameters.defaultRate),
  });

  return parseResponse(updateOrganizationUserResponseSchema, response.data, {
    schemaName: 'UpdateOrganizationUser',
    endpoint: url,
  });
}
